"use client";

import { createContext, useContext, useMemo } from "react";
import { useAuthContext } from "@/contexts/common/AuthProvider";
import { Permissions, RoleData } from "@/enums/user";
import { RoleAction } from "@/types/user";

type PermissionsContextType = {
  canAccess: (dataType: RoleData, level: Permissions) => boolean;
  /** For surfaces that span several pages' resources, e.g. one nav entry over multiple tabs. */
  canAccessAny: (dataTypes: readonly RoleData[], level: Permissions) => boolean;
  permissions: Record<RoleData, RoleAction> | null;
  isLoading: boolean;
};

const PermissionsContext = createContext<PermissionsContextType | undefined>(undefined);

export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    throw new Error("usePermissions must be used within PermissionsContextProvider");
  }
  return context;
};

// ---------------------------------------------------------------------------
// Decode compact permission format from auth seed
// Format: { "inbound": "crud", "settings": "", ... }
// ---------------------------------------------------------------------------

function decodePermissions(encoded: Record<string, string>): Record<RoleData, RoleAction> {
  const result = {} as Record<RoleData, RoleAction>;
  for (const dataType of Object.values(RoleData)) {
    const val = encoded[dataType] ?? "";
    result[dataType] = {
      create: val.includes("c"),
      read: val.includes("r"),
      update: val.includes("u"),
      delete: val.includes("d"),
    };
  }
  return result;
}

const mapPermissionToAction = (level: Permissions, actions: RoleAction): boolean => {
  switch (level) {
    case Permissions.Create:
      return actions.create;
    case Permissions.Read:
      return actions.read;
    case Permissions.Update:
      return actions.update;
    case Permissions.Delete:
      return actions.delete;
    default:
      return false;
  }
};

// ---------------------------------------------------------------------------
// Hook — reads permissions from auth context (provided by server seed)
// ---------------------------------------------------------------------------

const usePermissionsFunction = (): PermissionsContextType => {
  const { permissions: encodedPermissions } = useAuthContext();

  const permissions = useMemo(() => {
    if (!encodedPermissions || Object.keys(encodedPermissions).length === 0) {
      return null;
    }
    return decodePermissions(encodedPermissions);
  }, [encodedPermissions]);

  const canAccess = useMemo(() => {
    return (dataType: RoleData, level: Permissions): boolean => {
      if (!permissions) return false;
      const actions = permissions[dataType];
      if (!actions) return false;
      return mapPermissionToAction(level, actions);
    };
  }, [permissions]);

  const canAccessAny = useMemo(() => {
    return (dataTypes: readonly RoleData[], level: Permissions): boolean =>
      dataTypes.some((dataType) => canAccess(dataType, level));
  }, [canAccess]);

  return {
    canAccess,
    canAccessAny,
    permissions,
    isLoading: false, // Permissions arrive with the auth seed — never loading
  };
};

export const PermissionsContextProvider = ({
  children,
}: {
  readonly children: React.ReactNode;
}) => {
  const permissions = usePermissionsFunction();

  return <PermissionsContext.Provider value={permissions}>{children}</PermissionsContext.Provider>;
};
